/**
 * Rekenregels voor creditnota's — één bron voor de admin, de server en het dashboard.
 *
 * **Waarom dit apart staat.** Een creditnota raakt drie dingen die uit elkaar kunnen lopen: wat er
 * op de nota staat, wat er via Mollie terug moet, en wat het dashboard en de boekhoud-export laten
 * zien. Reken je dat op drie plekken uit, dan verschillen ze vroeg of laat een cent — en juist bij
 * een creditnota valt dat op, want de boekhouder telt facturen en creditnota's tegen elkaar weg.
 *
 * **Btw opnieuw berekenen, niet naar rato delen.** De verleiding is om bij 1 van de 3 stuks een
 * derde van de btw te nemen. Dat loopt een cent uit de pas zodra het bedrag niet deelbaar is. We
 * rekenen daarom met dezelfde motor als de order zelf: regels optellen, verzending erbij, dán btw.
 * Het btw-tarief komt van de órder (0 bij een verlegde intracommunautaire levering), niet van een
 * aanname — anders krijgt een Belgische zakelijke klant ineens 21% teruggestort die hij nooit
 * betaald heeft.
 */
import { calculateOrderTotalsWithShipping } from "../pricing/shipping";
import type { CreditNote, CreditNoteLine, Order, OrderTotals } from "../types/order";

/** Het btw-tarief van deze order; ontbreekt het veld (orders van vóór 03-09-2026) dan gold NL. */
export function btwTariefVan(order: Pick<Order, "btw">): number | undefined {
  return order.btw ? order.btw.tarief : undefined;
}

/**
 * Hoeveel stuks er per orderregel al gecrediteerd zijn. De sleutel is de index in `order.items`.
 * Een lege map betekent: er is nog niets gecrediteerd.
 */
export function gecrediteerdPerRegel(order: Pick<Order, "credits">): Map<number, number> {
  const per = new Map<number, number>();
  for (const nota of order.credits ?? []) {
    for (const r of nota.lines) per.set(r.itemIndex, (per.get(r.itemIndex) ?? 0) + r.quantity);
  }
  return per;
}

/** Hoeveel stuks van deze regel nog gecrediteerd kúnnen worden. */
export function nogTeCrediteren(order: Pick<Order, "items" | "credits">, itemIndex: number): number {
  const besteld = order.items?.[itemIndex]?.quantity ?? 0;
  return Math.max(0, besteld - (gecrediteerdPerRegel(order).get(itemIndex) ?? 0));
}

/** Hoeveel van de verzendkosten (excl. btw) al gecrediteerd is. */
export function gecrediteerdeVerzendingExcl(order: Pick<Order, "credits">): number {
  return (order.credits ?? []).reduce((s, n) => s + n.shippingExclCents, 0);
}

/** Wat er van de verzendkosten (excl. btw) nog terugbetaald kan worden. */
export function nogTeCrediterenVerzending(order: Pick<Order, "totals" | "credits">): number {
  return Math.max(0, (order.totals?.shipping ?? 0) - gecrediteerdeVerzendingExcl(order));
}

/** Som van alle opgemaakte creditnota's, incl. btw. Positief bedrag. */
export function gecrediteerdTotaal(order: Pick<Order, "credits">): number {
  return (order.credits ?? []).reduce((s, n) => s + n.totals.total, 0);
}

/**
 * Wat er van deze order netto is overgebleven, incl. btw — het factuurtotaal min alle creditnota's.
 * Dit is wat de klant per saldo betaald heeft.
 */
export function nettoOrderTotaal(order: Pick<Order, "totals" | "credits">): number {
  return (order.totals?.total ?? 0) - gecrediteerdTotaal(order);
}

/** Som van alle opgemaakte creditnota's, EXCLUSIEF btw. Positief bedrag. */
export function gecrediteerdTotaalExcl(order: Pick<Order, "credits">): number {
  return (order.credits ?? []).reduce((s, n) => s + (n.totals.total - (n.totals.vat ?? 0)), 0);
}

/**
 * Hetzelfde netto bedrag, maar EXCLUSIEF btw — dit hoort in de omzet.
 *
 * Btw is geen omzet: dat bedrag int je voor de Belastingdienst en draag je weer af. Het dashboard
 * telde het factuurtotaal inclusief btw op als "omzet", waardoor het cijfer 21% te hoog stond en
 * niet aansloot op de boekhouding. (Thijs, 12-10-2026.)
 */
export function nettoOrderTotaalExcl(order: Pick<Order, "totals" | "credits">): number {
  const totals = order.totals;
  const excl = (totals?.total ?? 0) - (totals?.vat ?? 0);
  return excl - gecrediteerdTotaalExcl(order);
}

/** Is er voor het volle factuurbedrag gecrediteerd? Dan is de order feitelijk teruggedraaid. */
export function volledigGecrediteerd(order: Pick<Order, "totals" | "credits">): boolean {
  const totaal = order.totals?.total ?? 0;
  return totaal > 0 && gecrediteerdTotaal(order) >= totaal;
}

/** Wat de invoer in het creditnota-scherm oplevert, vóórdat er een nota van gemaakt wordt. */
export interface CreditNotaInvoer {
  /** Aantal per orderregel-index; regels met 0 (of afwezig) tellen niet mee. */
  aantallen: Record<number, number>;
  /** Terug te betalen verzendkosten excl. btw. */
  shippingExclCents: number;
}

/** Waarom een concept-creditnota (nog) niet klopt — of null als hij klopt. */
export type CreditNotaFout =
  | { code: "leeg"; tekst: string }
  | { code: "teveel-stuks"; tekst: string; itemIndex: number }
  | { code: "teveel-verzending"; tekst: string }
  | { code: "boven-factuur"; tekst: string };

/** Een concept-creditnota: de regels, de bedragen en wat er eventueel niet aan klopt. */
export interface CreditNotaConcept {
  lines: CreditNoteLine[];
  shippingExclCents: number;
  totals: OrderTotals;
  fout: CreditNotaFout | null;
}

/**
 * Bouwt en controleert een concept-creditnota. Zowel het admin-scherm (om live het bedrag en een
 * eventuele fout te tonen) als de server (om de invoer te valideren) gebruiken deze functie — de
 * server vertrouwt dus nooit op de bedragen die de browser meestuurt.
 */
export function maakCreditNotaConcept(
  order: Pick<Order, "items" | "totals" | "credits" | "btw">,
  invoer: CreditNotaInvoer,
): CreditNotaConcept {
  const items = order.items ?? [];
  const lines: CreditNoteLine[] = [];
  let fout: CreditNotaFout | null = null;

  for (const [sleutel, aantal] of Object.entries(invoer.aantallen)) {
    const itemIndex = Number(sleutel);
    if (!Number.isInteger(itemIndex) || aantal <= 0) continue;
    const item = items[itemIndex];
    if (!item) continue;
    const over = nogTeCrediteren(order, itemIndex);
    if (aantal > over) {
      fout ??= {
        code: "teveel-stuks",
        itemIndex,
        tekst: over === 0
          ? `"${item.productName}" is al volledig gecrediteerd.`
          : `Van "${item.productName}" ${over === 1 ? "is" : "zijn"} nog maar ${over} stuks te crediteren.`,
      };
      continue;
    }
    lines.push({
      itemIndex,
      quantity: aantal,
      unitPriceExclCents: item.unitPrice ?? 0,
      productName: item.productName ?? "",
    });
  }

  const maxVerzending = nogTeCrediterenVerzending(order);
  const shippingExclCents = Math.max(0, Math.round(invoer.shippingExclCents || 0));
  if (shippingExclCents > maxVerzending) {
    fout ??= {
      code: "teveel-verzending",
      tekst: `Van de verzendkosten is nog maar ${(maxVerzending / 100).toFixed(2).replace(".", ",")} euro te crediteren.`,
    };
  }

  // Dezelfde motor als de order: regels optellen, verzending erbij, dán btw over het geheel.
  // Geen korting: die zat al in het factuurbedrag verwerkt en wordt niet nog eens verrekend.
  const gerekend = calculateOrderTotalsWithShipping(
    lines.map((r) => r.unitPriceExclCents * r.quantity),
    Math.min(shippingExclCents, maxVerzending),
    null,
    btwTariefVan(order),
  );
  /**
   * De vier velden uitgeschreven in plaats van `gerekend` doorgeven.
   *
   * Reden (gevonden 25-09-2026, de eerste echte creditnota van Taco mislukte hierop): zonder
   * korting geeft de prijsmotor `discount: undefined` terug, en Firestore weigert `undefined` —
   * behalve waar de handle `ignoreUndefinedProperties` heeft staan, en dat heeft alleen
   * `createOrder`. Het wegschrijven van de nota klapte er dus op, zonder dat er iets zichtbaars
   * gebeurde: geen nummer, geen nota, geen status. Een creditnota kent geen korting, dus die
   * sleutel hoort er sowieso niet op.
   */
  const totals: OrderTotals = {
    subtotal: gerekend.subtotal,
    shipping: gerekend.shipping,
    vat: gerekend.vat,
    total: gerekend.total,
  };

  if (!fout && lines.length === 0 && shippingExclCents === 0) {
    fout = { code: "leeg", tekst: "Vul een aantal in bij minstens één regel, of een bedrag aan verzendkosten." };
  }
  // Laatste vangnet: alle creditnota's samen mogen nooit boven het factuurbedrag uitkomen.
  if (!fout && gecrediteerdTotaal(order) + totals.total > (order.totals?.total ?? 0)) {
    fout = { code: "boven-factuur", tekst: "Dit is samen met eerdere creditnota's meer dan het factuurbedrag." };
  }

  return { lines, shippingExclCents: Math.min(shippingExclCents, maxVerzending), totals, fout };
}

/**
 * Het creditnotanummer: `CN-2026-001`.
 *
 * Het jaartal zit erin en de teller begint elk jaar opnieuw bij 1 — zo is aan het nummer te zien
 * in welk boekjaar de nota thuishoort, wat een doorlopende reeks zonder jaartal niet laat zien.
 * Drie cijfers is ruim: bij meer dan 999 creditnota's in één jaar groeit het nummer gewoon mee.
 */
export function creditNotaNummer(jaar: number, volgnummer: number): string {
  return `CN-${jaar}-${String(volgnummer).padStart(3, "0")}`;
}

/** Alle creditnota's van een order, nieuwste eerst. */
export function creditsNieuwsteEerst(order: Pick<Order, "credits">): CreditNote[] {
  return [...(order.credits ?? [])].sort((a, b) => b.at - a.at);
}
